import IOriginalValuesData from '@/models/original/IOriginalValuesData';
import IOriginalValuesProperty from '@/models/original/IOriginalValuesProperty';
import utils from '@/utils/QSUtils';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import IOriginalValueAddressed from '@/models/original/IOriginalValueAddressed';
import IOriginalValuesPropertyAddressed from '@/models/original/IOriginalValuesPropertyAddressed';

// Internal structures - payload types for actions
//
interface IOriginalValueIndexed {
    index: number;
    originalValueAddressed: IOriginalValueAddressed;
}

// Internal only: The set of mutation method names as strings.
// Only those mutations called by actions within this store module are necessary.
// Actions call mutations via this.context.commit() with the first argument as the mutation method name.
const Mutate = {
    setProperty: 'setProperty',
    addValue: 'addValue',
    updateValue: 'updateValue',
};

@Module({ namespaced: true, name: 'originalValuesV36' })
export default class OriginalValuesStoreModuleV36 extends VuexModule {
    // Private are the store properties
    private originalValuesData: IOriginalValuesData = {};

    /**
     * Get the original values data
     */
    get OriginalValuesData(): IOriginalValuesData {
        return this.originalValuesData;
    }

    /**
     * Get an original values property by the given address1 and unit number.
     * Returns null when one does not exist.
     */
    get OriginalValuesByAddress1AndUnitNo() {
        return (address1AndUnitNo: string): IOriginalValuesProperty | null => {
            if (utils.isKeyInObject(this.originalValuesData, address1AndUnitNo)) {
                return this.originalValuesData[address1AndUnitNo];
            }
            return null;
        };
    }

    /**
     * Get the count of properties in the store.
     * The store state is a dictionary keyed by address1AndUnitNo.
     */
    get PropertyCount(): number {
        const keys = Object.keys(this.originalValuesData);
        return keys.length;
    }

    /**
     * Action: Add or replace the given original values property using the given address1AndUnitNo.
     * @param propertyAddressed
     * @returns true on add, false on replace
     */
    @Action
    async addOrReplaceProperty(propertyAddressed: IOriginalValuesPropertyAddressed): Promise<boolean> {
        const exists = utils.isKeyInObject(this.originalValuesData, propertyAddressed.address1AndUnitNo);

        this.context.commit(Mutate.setProperty, propertyAddressed);

        return !exists;
    }

    /**
     * Action: Add or replace the given original value in the property identified by the given address1AndUnitNo.
     * Will create a new original values property for the given address1AndUnitNo if one doesn't exist.
     * @param address1AndUnitNo
     * @param originalValue
     * @returns true on added, false on replaced or unexpected issue
     */
    @Action
    async addOrReplaceValue(valueAddressed: IOriginalValueAddressed): Promise<boolean> {
        // First make certain there's an OriginalValuesProperty in the store state for the address
        let originalValuesProperty = this.OriginalValuesByAddress1AndUnitNo(valueAddressed.address1AndUnitNo);
        if (!originalValuesProperty) {
            // Create and save a new property with the address
            const addressedProperty: IOriginalValuesPropertyAddressed = {
                address1AndUnitNo: valueAddressed.address1AndUnitNo,
                property: [],
            };
            this.context.commit(Mutate.setProperty, addressedProperty);

            originalValuesProperty = this.OriginalValuesByAddress1AndUnitNo(valueAddressed.address1AndUnitNo);
        }

        if (originalValuesProperty) {
            const valueIdx = originalValuesProperty.findIndex((ov) => ov.fieldId === valueAddressed.value.fieldId);
            if (valueIdx >= 0) {
                const originalValueIndexed: IOriginalValueIndexed = {
                    index: valueIdx,
                    originalValueAddressed: valueAddressed,
                };
                this.context.commit(Mutate.updateValue, originalValueIndexed);
            } else {
                this.context.commit(Mutate.addValue, valueAddressed);
                return true;
            }
        }

        return false;
    }

    /**
     * Mutation: Replace any existing set of original values data with the given set of original values data.
     * This is the entire store's state which is a dictionary of IOriginalValuesProperty keyed by address1AndUnitNo.
     * @param originalValuesData
     */
    @Mutation
    setAllData(originalValuesData: IOriginalValuesData) {
        this.originalValuesData = originalValuesData;
    }

    /**
     * Mutation: Sets the given property into the store's data with the given address1AndUnitNo.
     * Preference is to use the addOrReplaceProperty action.
     * @param addressedProperty
     */
    @Mutation
    setProperty(propertyAddressed: IOriginalValuesPropertyAddressed) {
        this.originalValuesData[propertyAddressed.address1AndUnitNo] = propertyAddressed.property;
    }

    /**
     * Mutation: Adds or updates the given value into the store's data using the given address1AndUnitNo to identify the property.
     * Preference is to use the addOrReplaceValue action.
     * @param valueAddressed
     */
    @Mutation
    addValue(valueAddressed: IOriginalValueAddressed) {
        if (utils.isKeyInObject(this.originalValuesData, valueAddressed.address1AndUnitNo)) {
            const originalValuesProperty = this.originalValuesData[valueAddressed.address1AndUnitNo];

            originalValuesProperty.push(valueAddressed.value);
            // console.log(`OriginalValuesStore.addValue(), Added to[${valueAddressed.address1AndUnitNo}] with [${valueAddressed.value.originalValue}] for field[${valueAddressed.value.fieldId}]`);
        }
    }

    /**
     * Mutation: Updates the given value into the store's data using the given address1AndUnitNo to identify the property.
     * Preference is to use the addOrReplaceValue action.
     * @param originalValueIndexed
     */
    @Mutation
    updateValue(originalValueIndexed: IOriginalValueIndexed) {
        const index = originalValueIndexed.index;
        const originalValueAddressed = originalValueIndexed.originalValueAddressed;

        if (utils.isKeyInObject(this.originalValuesData, originalValueAddressed.address1AndUnitNo)) {
            const originalValuesProperty = this.originalValuesData[originalValueAddressed.address1AndUnitNo];

            const currentValue = originalValuesProperty[index];
            if (currentValue.originalValue !== originalValueAddressed.value.originalValue) {
                // Only replace if the store's value will change, the manager handles case checking so the store doesn't
                originalValuesProperty[index] = originalValueAddressed.value;
                // console.log(`OriginalValuesStore.updateValue(), Updated[${originalValueAddressed.address1AndUnitNo}] with [${originalValueAddressed.value.originalValue}] from[${currentValue.originalValue}] for field[${originalValueAddressed.value.fieldId}]`);
            }
        }
    }

    /**
     * Mutation: Deletes the given value from the store's data using the given address1AndUnitNo to identify the property.
     * The property may or may not exist, will not create it. Also, the value, identified by field id, may not exist.
     * @param valueAddressed
     * @returns true on deleted, false when the OriginalValue (by fieldId) or OriginalValuesProperty (by address1AndUnitNo) aren't found in the store
     */
    @Mutation
    deleteValue(valueAddressed: IOriginalValueAddressed): boolean {
        if (utils.isKeyInObject(this.originalValuesData, valueAddressed.address1AndUnitNo)) {
            const originalValuesProperty = this.originalValuesData[valueAddressed.address1AndUnitNo];

            const valueIdx = originalValuesProperty.findIndex((ov) => ov.fieldId === valueAddressed.value.fieldId);
            if (valueIdx >= 0) {
                const deletedValues = originalValuesProperty.splice(valueIdx, 1);
                // console.log(`OriginalValuesStore.deleteValue(), Deleted from[${valueAddressed.address1AndUnitNo}] with [${deletedValues[0].originalValue}] for field[${valueAddressed.value.fieldId}]`);
                return true;
            }
        }
        return false;
    }

    /**
     * Mutation: Reset all state.
     */
    @Mutation
    resetState() {
        this.originalValuesData = {};
    }
}
